import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { SDUIFlowLayoutItem } from "@faire//retailer-visitor-shared/src/components/SDUI/LayoutRenderers/FlowLayout/SDUIFlowLayoutItem";
import { getResponsiveSpacingPixels } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/spacing";
import { getColSpanFromItemWidths, getItemOrders, getItemWidths, getLCMOfNumColumnsGivenWidths, NUM_COLUMNS_GRID, NUM_COLUMNS_GRID_DESKTOP } from "@faire/retailer-visitor-shared/components/SDUI/LayoutRenderers/FlowLayout/flowLayoutCalculationUtils";
import { assignInlineVars, mapResponsivePropToCSSVars } from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import classNames from "classnames";
import React from "react";
var varMobileRowGap = {
    variable: "--f-sdui-fl-rg-m",
    className: "gap-y-(--f-sdui-fl-rg-m)"
};
var varTabletRowGap = {
    variable: "--f-sdui-fl-rg-md",
    className: "md:gap-y-(--f-sdui-fl-rg-md)"
};
var varDesktopRowGap = {
    variable: "--f-sdui-fl-rg-lg",
    className: "lg:gap-y-(--f-sdui-fl-rg-lg)"
};
var varXLargeRowGap = {
    variable: "--f-sdui-fl-rg-xl",
    className: "xl:gap-y-(--f-sdui-fl-rg-xl)"
};
var varMobileColumnGap = {
    variable: "--f-sdui-fl-cg-m",
    className: "gap-x-(--f-sdui-fl-cg-m)"
};
var varTabletColumnGap = {
    variable: "--f-sdui-fl-cg-md",
    className: "md:gap-x-(--f-sdui-fl-cg-md)"
};
var varDesktopColumnGap = {
    variable: "--f-sdui-fl-cg-lg",
    className: "lg:gap-x-(--f-sdui-fl-cg-lg)"
};
var varXLargeColumnGap = {
    variable: "--f-sdui-fl-cg-xl",
    className: "xl:gap-x-(--f-sdui-fl-cg-xl)"
};
var varMobileNumColumns = {
    variable: "--f-sdui-fl-nc-m",
    className: "grid-cols-(--f-sdui-fl-nc-m)"
};
var varTabletNumColumns = {
    variable: "--f-sdui-fl-nc-md",
    className: "md:grid-cols-(--f-sdui-fl-nc-md)"
};
var varDesktopNumColumns = {
    variable: "--f-sdui-fl-nc-lg",
    className: "lg:grid-cols-(--f-sdui-fl-nc-lg)"
};
var varXLargeNumColumns = {
    variable: "--f-sdui-fl-nc-xl",
    className: "xl:grid-cols-(--f-sdui-fl-nc-xl)"
};
export var SDUIFlowLayoutRenderer = function(param) {
    var flowLayout = param.flowLayout, items = param.items;
    var itemWidths = flowLayout.component_widths;
    var itemOrders = undefined;
    var numColumnsPerRow = undefined;
    if (flowLayout.responsive_component_layout_configs) {
        itemOrders = {
            mobile_and_above: getItemOrders(flowLayout.responsive_component_layout_configs.mobile_and_above),
            tablet_and_above: getItemOrders(flowLayout.responsive_component_layout_configs.tablet_and_above),
            desktop_and_above: getItemOrders(flowLayout.responsive_component_layout_configs.desktop_and_above),
            x_large_and_above: getItemOrders(flowLayout.responsive_component_layout_configs.x_large_and_above)
        };
        // We assume that we only return as many configs as number of visible items.
        // This is important because itemWidths also has this constraint.
        itemWidths = {
            mobile_and_above: getItemWidths(flowLayout.responsive_component_layout_configs.mobile_and_above),
            tablet_and_above: getItemWidths(flowLayout.responsive_component_layout_configs.tablet_and_above),
            desktop_and_above: getItemWidths(flowLayout.responsive_component_layout_configs.desktop_and_above),
            x_large_and_above: getItemWidths(flowLayout.responsive_component_layout_configs.x_large_and_above)
        };
        var _itemWidths_mobile_and_above, _itemWidths_tablet_and_above, _itemWidths_desktop_and_above, _itemWidths_x_large_and_above;
        numColumnsPerRow = {
            mobile_and_above: getLCMOfNumColumnsGivenWidths((_itemWidths_mobile_and_above = itemWidths.mobile_and_above) !== null && _itemWidths_mobile_and_above !== void 0 ? _itemWidths_mobile_and_above : []),
            tablet_and_above: getLCMOfNumColumnsGivenWidths((_itemWidths_tablet_and_above = itemWidths.tablet_and_above) !== null && _itemWidths_tablet_and_above !== void 0 ? _itemWidths_tablet_and_above : []),
            desktop_and_above: getLCMOfNumColumnsGivenWidths((_itemWidths_desktop_and_above = itemWidths.desktop_and_above) !== null && _itemWidths_desktop_and_above !== void 0 ? _itemWidths_desktop_and_above : []),
            x_large_and_above: getLCMOfNumColumnsGivenWidths((_itemWidths_x_large_and_above = itemWidths.x_large_and_above) !== null && _itemWidths_x_large_and_above !== void 0 ? _itemWidths_x_large_and_above : [])
        };
    }
    var gapStyles = getFlowLayoutGapStyles(flowLayout);
    if (!itemWidths || items.length === 0) {
        return null;
    }
    var itemNodes = renderFlowLayoutItems(items, itemWidths, itemOrders, numColumnsPerRow);
    var _numColumnsPerRow_mobile_and_above, _numColumnsPerRow_tablet_and_above, _numColumnsPerRow_desktop_and_above, _numColumnsPerRow_x_large_and_above;
    var _obj;
    return /*#__PURE__*/ _jsx("div", {
        "data-test-id": "flow-layout-container",
        className: classNames("grid w-full grid-flow-row", varMobileNumColumns.className, varTabletNumColumns.className, varDesktopNumColumns.className, varXLargeNumColumns.className, gapStyles.rowSpacingClasses, gapStyles.columnSpacingClasses),
        style: assignInlineVars(_object_spread((_obj = {}, _define_property(_obj, varMobileNumColumns.variable, "repeat(".concat((_numColumnsPerRow_mobile_and_above = numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.mobile_and_above) !== null && _numColumnsPerRow_mobile_and_above !== void 0 ? _numColumnsPerRow_mobile_and_above : NUM_COLUMNS_GRID, ", 1fr)")), _define_property(_obj, varTabletNumColumns.variable, "repeat(".concat((_numColumnsPerRow_tablet_and_above = numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.tablet_and_above) !== null && _numColumnsPerRow_tablet_and_above !== void 0 ? _numColumnsPerRow_tablet_and_above : NUM_COLUMNS_GRID, ", 1fr)")), _define_property(_obj, varDesktopNumColumns.variable, "repeat(".concat((_numColumnsPerRow_desktop_and_above = numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.desktop_and_above) !== null && _numColumnsPerRow_desktop_and_above !== void 0 ? _numColumnsPerRow_desktop_and_above : NUM_COLUMNS_GRID_DESKTOP, ", 1fr)")), _define_property(_obj, varXLargeNumColumns.variable, "repeat(".concat((_numColumnsPerRow_x_large_and_above = numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.x_large_and_above) !== null && _numColumnsPerRow_x_large_and_above !== void 0 ? _numColumnsPerRow_x_large_and_above : NUM_COLUMNS_GRID_DESKTOP, ", 1fr)")), _obj), gapStyles.columnSpacingVars, gapStyles.rowSpacingVars)),
        children: itemNodes
    });
};
var renderFlowLayoutItems = function(items, itemWidths, itemOrders, numColumnsPerRow) {
    var itemNodes = items.reduce(function(accValue, item) {
        var _getColSpanFromItemWidths = getColSpanFromItemWidths(itemWidths, item.itemVisibility, accValue.currentWidthIndices, numColumnsPerRow), colSpan = _getColSpanFromItemWidths.colSpan, newWidthIndices = _getColSpanFromItemWidths.newWidthIndices;
        var itemOrder = undefined;
        if (itemOrders) {
            var _itemOrders_mobile_and_above, _itemOrders_tablet_and_above, _itemOrders_desktop_and_above, _itemOrders_x_large_and_above;
            itemOrder = {
                mobile_and_above: (_itemOrders_mobile_and_above = itemOrders.mobile_and_above) === null || _itemOrders_mobile_and_above === void 0 ? void 0 : _itemOrders_mobile_and_above[accValue.currentWidthIndices.mobile],
                tablet_and_above: (_itemOrders_tablet_and_above = itemOrders.tablet_and_above) === null || _itemOrders_tablet_and_above === void 0 ? void 0 : _itemOrders_tablet_and_above[accValue.currentWidthIndices.tablet],
                desktop_and_above: (_itemOrders_desktop_and_above = itemOrders.desktop_and_above) === null || _itemOrders_desktop_and_above === void 0 ? void 0 : _itemOrders_desktop_and_above[accValue.currentWidthIndices.desktop],
                x_large_and_above: (_itemOrders_x_large_and_above = itemOrders.x_large_and_above) === null || _itemOrders_x_large_and_above === void 0 ? void 0 : _itemOrders_x_large_and_above[accValue.currentWidthIndices.xl_desktop]
            };
        }
        var itemNode = /*#__PURE__*/ _jsx(SDUIFlowLayoutItem, {
            itemColSpans: colSpan,
            item: item,
            itemOrder: itemOrder
        }, item.itemKey);
        return {
            currentWidthIndices: newWidthIndices,
            itemNodes: _to_consumable_array(accValue.itemNodes).concat([
                itemNode
            ])
        };
    }, INITIAL_ACC_VALUE).itemNodes;
    return itemNodes;
};
var INITIAL_ACC_VALUE = {
    currentWidthIndices: {
        mobile: 0,
        tablet: 0,
        desktop: 0,
        xl_desktop: 0
    },
    itemNodes: []
};
var getFlowLayoutGapStyles = function(flowLayout) {
    var clientRowSpacing = getResponsiveSpacingPixels(Spacing.R, flowLayout.row_spacing);
    var clientColumnSpacing = getResponsiveSpacingPixels(Spacing.R, flowLayout.column_spacing);
    var _mapResponsivePropToCSSVars = mapResponsivePropToCSSVars(clientRowSpacing, {
        mobileAndAbove: varMobileRowGap,
        tabletAndAbove: varTabletRowGap,
        desktopAndAbove: varDesktopRowGap,
        xLargeAndAbove: varXLargeRowGap
    }), rowSpacingVars = _mapResponsivePropToCSSVars.vars, rowSpacingClasses = _mapResponsivePropToCSSVars.classes;
    var _mapResponsivePropToCSSVars1 = mapResponsivePropToCSSVars(clientColumnSpacing, {
        mobileAndAbove: varMobileColumnGap,
        tabletAndAbove: varTabletColumnGap,
        desktopAndAbove: varDesktopColumnGap,
        xLargeAndAbove: varXLargeColumnGap
    }), columnSpacingVars = _mapResponsivePropToCSSVars1.vars, columnSpacingClasses = _mapResponsivePropToCSSVars1.classes;
    return {
        rowSpacingClasses: rowSpacingClasses,
        rowSpacingVars: rowSpacingVars,
        columnSpacingClasses: columnSpacingClasses,
        columnSpacingVars: columnSpacingVars
    };
};
